import { ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { RuleFindingTrend, RuleFindingTrendFilters } from '@/types/models'

// Singleton, following useRepoScans.ts's shape: shared state so the view
// retains its list across navigation within a session.
const trends = ref<RuleFindingTrend[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Guards against a stale filter request's response landing after a newer
// one's and clobbering it.
const requestGuard = createRequestGuard()

export function useRuleFindingTrends() {
  async function fetchRuleFindingTrends(filters: RuleFindingTrendFilters = {}): Promise<void> {
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load rule finding trends.',
      async () => {
        const data = await githubApi.listRuleFindingTrends(filters)
        if (!requestGuard.isCurrent(token)) return
        trends.value = data
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  // Called on logout so the next session's view doesn't briefly show the
  // previous user's data before its own fetch resolves, and so a late
  // response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    trends.value = []
    loading.value = false
    error.value = null
  }

  return {
    trends,
    loading,
    error,
    fetchRuleFindingTrends,
    reset,
  }
}
